import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import ChallengeStatus from "@Enums/ChallengeStatus";
import { Box, CircularProgress, Stack, Typography, capitalize, useTheme } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import { Variant } from "@mui/material/styles/createTypography";
import { isString } from "lodash-es";
import BybConditionalWrapper from "../byb-conditional-wrapper";

enum ButtonColor {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

interface IBybButton extends ButtonProps {
  icon?: React.ReactNode;
  fontVariant?: string;
  color?: `${ButtonColor}`;
  status?: `${ChallengeStatus}`;
  loading?: boolean;
  loadingText?: string;
}

const BybButton = ({
  icon,
  fontVariant,
  children,
  size = "medium",
  color = ButtonColor.PRIMARY,
  status,
  disabled,
  sx,
  loading,
  loadingText,
  fullWidth = false, // Default to false
  ...props
}: IBybButton) => {
  const theme = useTheme();
  const textColor = color === ButtonColor.PRIMARY ? "common.white" : "dsBase.v200";

  let circularProgressSize = 20;
  if (size === "large") {
    circularProgressSize = 24;
  } else if (size === "small") {
    circularProgressSize = 20;
  }

  const buttonSizeConfig = {
    small: {
      textVariant: "textS500",
      padding: theme.spacing(1.25, 1.75),
      borderRadius: "8px",
    },
    medium: {
      textVariant: "textS600",
      padding: theme.spacing(1.75, 2.5),
      borderRadius: "10px",
    },
    large: {
      textVariant: "textBase600",
      padding: theme.spacing(2, 3.5),
      borderRadius: "12px",
    },
  };

  const buttonStatusConfig = {
    [ChallengeStatus.NOT_STARTED]: {
      backgroundColor: "dsSurface.v100",
      color: "dsNeutral.v400",
    },
    [ChallengeStatus.IN_PROGRESS]: {
      backgroundColor: "dsSecondaryYellow.v200",
      color: "dsSecondaryYellow.v400",
    },
    [ChallengeStatus.COMPLETED]: {
      backgroundColor: "dsSuccess.v200",
      color: "dsSuccess.v400",
    },
    [ChallengeStatus.FAILED]: {
      backgroundColor: "dsDestructive.v100",
      color: "dsDestructive.v400",
    },
    [ChallengeStatus.WINNER]: {
      backgroundColor: "dsWarning.v100",
      color: "dsWarning.v300",
    },
  };

  const styles = {
    borderRadius: buttonSizeConfig[size].borderRadius,
    backgroundColor: color === ButtonColor.PRIMARY ? "dsPrimary.v300" : "dsSurface.v300",
    textTransform: "none",
    width: fullWidth ? "100%" : "auto",
    padding: buttonSizeConfig[size].padding,
    ":hover": {
      backgroundColor: color === ButtonColor.PRIMARY ? "dsPrimary.v400" : "dsSurface.v200",
    },
    ":disabled": {
      opacity: 0.5,
      backgroundColor: status ? buttonStatusConfig[status].backgroundColor : "dsSurface.v400",
    },
    ...sx,
  };

  return (
    <BybConditionalWrapper
      condition={!!props.onClick || props.type === "submit" || props.type === "button"}
      trueWrapper={children => (
        <HapticsButtonWrapper>
          <Button disableElevation variant="contained" sx={styles} disabled={!!status || loading || disabled} {...props}>
            {children}
          </Button>
        </HapticsButtonWrapper>
      )}
      falseWrapper={children => <Box sx={styles}>{children}</Box>}
    >
      {!loading ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon && (
            <Box
              sx={{
                "& > *": {
                  display: "flex",
                  color: status ? buttonStatusConfig[status].color : textColor,
                },
              }}
            >
              {icon}
            </Box>
          )}
          {children && (
            <Typography
              variant={(fontVariant as Variant) || (buttonSizeConfig[size].textVariant as Variant)}
              color={status ? buttonStatusConfig[status].color : textColor}
            >
              {isString(children) ? capitalize(children) : children}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={circularProgressSize} sx={{ color: textColor }} />
          {loadingText && (
            <Typography variant="textS500" color={textColor}>
              {loadingText}
            </Typography>
          )}
        </Stack>
      )}
    </BybConditionalWrapper>
  );
};

export default BybButton;
